import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  ArrowRight,
  ArrowUp,
  Mail,
  MapPin,
  Facebook,
  Instagram,
  Youtube,
  Linkedin,
  ChevronDown,
} from "lucide-react";
import {
  FOOTER_QUICK_LINKS,
  FOOTER_PARTNER_LINKS,
  FOOTER_PROGRAMS,
  CONTACT_INFO,
} from "@/lib/constants";
import { LogoLockup } from "@/components/Navigation";
import { useEnquiry } from "@/lib/enquiry-context";

const SOCIALS = [
  { icon: Facebook, label: "Facebook", href: "#top" },
  { icon: Instagram, label: "Instagram", href: "https://www.instagram.com/carltonwellnessgroup" },
  { icon: Youtube, label: "YouTube", href: "#top" },
  { icon: Linkedin, label: "LinkedIn", href: "#top" },
];

const colLink =
  "text-[13px] text-[#f7f2e8]/70 hover:text-[#c9a14a] transition-colors duration-300";

function useIsDesktop() {
  const query = "(min-width: 768px)";
  const get = () => (typeof window !== "undefined" ? window.matchMedia(query).matches : true);
  const [isDesktop, setIsDesktop] = useState(get);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (e) => setIsDesktop(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return isDesktop;
}

function FooterColumn({ title, testId, isDesktop, children }) {
  const [open, setOpen] = useState(false);
  const expanded = isDesktop || open;
  return (
    <div className="border-b border-[#f7f2e8]/10 md:border-0" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={expanded}
        aria-controls={`${testId}-list`}
        data-testid={`${testId}-toggle`}
        className="w-full flex items-center justify-between py-4 md:py-0 md:pointer-events-none"
      >
        <p className="overline text-[#c9a14a]">{title}</p>
        <ChevronDown
          size={16}
          strokeWidth={1.8}
          className={`text-[#c9a14a] md:hidden transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <ul
        id={`${testId}-list`}
        className={`space-y-3.5 md:mt-6 md:pb-0 md:block ${expanded ? "block pb-5" : "hidden"}`}
      >
        {children}
      </ul>
    </div>
  );
}

export default function Footer() {
  const { openBook } = useEnquiry();
  const isDesktop = useIsDesktop();
  const scrollTop = () => {
    if (window.__lenis) window.__lenis.scrollTo(0, { duration: 1.4 });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer data-testid="site-footer" className="relative bg-[#0b0b0b] border-t border-[#c9a14a]/25 overflow-hidden">
      <div className="absolute inset-0 grain pointer-events-none" aria-hidden="true" />
      <div className="relative max-w-[1480px] mx-auto px-5 md:px-10 pt-16 md:pt-20 pb-8">
        <div className="grid sm:grid-cols-2 xl:grid-cols-[1.35fr_0.9fr_1.05fr_1fr_1.15fr] gap-12">
          <div>
            <LogoLockup />
            <p className="mt-5 text-[13px] text-[#f7f2e8]/65 leading-relaxed max-w-xs">
              India's premium protocol-led wellness network for prevention, recovery,
              slimming, anti-ageing, mental wellbeing and lifestyle health.
            </p>
            <a
              href="https://theebg.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-ebg-badge"
              className="mt-5 flex items-center gap-4 border border-[#c9a14a]/35 rounded-xl px-5 py-4 max-w-[280px] bg-[#050505] hover:border-[#c9a14a]/70 transition-colors duration-300 group"
            >
              <img src="/assets/ebg-logo.png" alt="EBG Group" className="h-9 w-auto shrink-0" />
              <span className="leading-snug">
                <span className="block font-serif-display text-[17px] text-[#f7f2e8]">
                  An <span className="text-[#c9a14a]">EBG</span> Group
                  <br />
                  Company
                </span>
                <span className="mt-1 block text-[11px] text-[#f7f2e8]/55 group-hover:text-[#c9a14a] transition-colors">
                  theebg.com ↗
                </span>
              </span>
            </a>
            <div className="mt-7 flex gap-3">
              {SOCIALS.map(({ icon: Icon, label, href }) => {
                const isExternal = href && href !== "#top";
                return (
                  <a
                    key={label}
                    href={href}
                    onClick={(e) => {
                      if (!isExternal) e.preventDefault();
                    }}
                    target={isExternal ? "_blank" : undefined}
                    rel={isExternal ? "noopener noreferrer" : undefined}
                    aria-label={label}
                    data-testid={`footer-social-${label.toLowerCase()}`}
                    className="w-10 h-10 rounded-full bg-[#f7f2e8]/10 flex items-center justify-center text-[#f7f2e8] hover:bg-[#c9a14a] hover:text-[#050505] transition-colors duration-300"
                  >
                    <Icon size={16} strokeWidth={1.8} />
                  </a>
                );
              })}
            </div>
          </div>

          <FooterColumn title="Quick Links" testId="footer-col-quick-links" isDesktop={isDesktop}>
            {FOOTER_QUICK_LINKS.map((l) => (
              <li key={l.label}>
                <Link
                  to={l.to}
                  data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={colLink}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </FooterColumn>

          <FooterColumn title="Programs" testId="footer-col-programs" isDesktop={isDesktop}>
            {FOOTER_PROGRAMS.map((p) => (
              <li key={p.label}>
                <Link to={p.to} data-testid={`footer-program-${p.to.split("/").pop()}`} className={colLink}>
                  {p.label}
                </Link>
              </li>
            ))}
          </FooterColumn>

          <FooterColumn title="For Partners" testId="footer-col-partners" isDesktop={isDesktop}>
            {FOOTER_PARTNER_LINKS.map((l) => (
              <li key={l.label}>
                <Link
                  to={l.to}
                  data-testid={`footer-partner-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={colLink}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </FooterColumn>

          <div>
            <p className="overline text-[#c9a14a]">Contact Us</p>
            <div className="mt-6 space-y-4">
              <a
                href={`mailto:${CONTACT_INFO.email}`}
                data-testid="footer-email"
                className="flex items-center gap-3 text-[#f7f2e8] text-base font-semibold hover:text-[#c9a14a] transition-colors"
              >
                <Mail size={15} className="text-[#c9a14a] shrink-0" />
                {CONTACT_INFO.email}
              </a>
              <p className="flex items-start gap-3 text-[13px] text-[#f7f2e8]/70 leading-relaxed">
                <MapPin size={15} className="text-[#c9a14a] shrink-0 mt-0.5" />
                {CONTACT_INFO.corporate}
              </p>
              <button
                onClick={() => openBook("franchise")}
                data-testid="footer-enquire-now-button"
                className="btn-gold mt-2"
              >
                Enquire Now <ArrowRight size={15} strokeWidth={2} />
              </button>
            </div>
          </div>
        </div>

        <div
          data-testid="footer-sdg-strip"
          className="mt-16 pt-10 border-t border-[#f7f2e8]/10 grid gap-10 md:grid-cols-[1fr_auto] md:items-center"
        >
          <div className="max-w-xl">
            <p className="overline text-[#c9a14a]">Our Commitment</p>
            <p className="mt-4 text-[13px] md:text-sm text-[#f7f2e8]/70 leading-relaxed">
              We are aligned to the United Nations Sustainable Development Goals —
              championing health, well-being and decent work &amp; economic growth
              across every Carlton salon.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-5 md:justify-end">
            <img
              src="/assets/sdg/sdg-logo.webp"
              alt="UN Sustainable Development Goals"
              data-testid="footer-sdg-logo"
              className="h-10 sm:h-12 md:h-14 w-auto"
              loading="lazy"
            />
            <img
              src="/assets/sdg/goal-3.jpg"
              alt="SDG 3 — Good Health and Well-Being"
              data-testid="footer-sdg-goal-3"
              className="h-16 sm:h-20 md:h-24 w-auto"
              loading="lazy"
            />
            <img
              src="/assets/sdg/goal-8.jpg"
              alt="SDG 8 — Decent Work and Economic Growth"
              data-testid="footer-sdg-goal-8"
              className="h-16 sm:h-20 md:h-24 w-auto"
              loading="lazy"
            />
          </div>
        </div>

        <div className="mt-12 pt-7 border-t border-[#f7f2e8]/10 flex flex-col md:flex-row items-center justify-between gap-5">
          <div className="text-center md:text-left">
            <p className="text-[12px] text-[#f7f2e8]/55" data-testid="footer-copyright">
              © 2026 Carlton Wellness Center. All Rights Reserved.
            </p>
            <p className="mt-1.5 text-[12px] text-[#f7f2e8]/45" data-testid="footer-credit">
              Design and Development by{" "}
              <a
                href="https://www.reachvel.com"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-reachvel-link"
                className="text-[#c9a14a] hover:text-[#e9d8a6] transition-colors underline underline-offset-4"
              >
                Reachvel
              </a>
            </p>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/privacy-policy" data-testid="footer-privacy-link" className="text-[12px] text-[#f7f2e8]/55 hover:text-[#c9a14a] transition-colors">
              Privacy Policy
            </Link>
            <span className="text-[#f7f2e8]/25">|</span>
            <Link to="/terms" data-testid="footer-terms-link" className="text-[12px] text-[#f7f2e8]/55 hover:text-[#c9a14a] transition-colors">
              Terms & Conditions
            </Link>
            <button
              onClick={scrollTop}
              aria-label="Back to top"
              data-testid="footer-scroll-top-button"
              className="w-11 h-11 rounded-full border border-[#c9a14a]/60 flex items-center justify-center text-[#c9a14a] hover:bg-[#c9a14a] hover:text-[#050505] transition-colors duration-300 ml-2"
            >
              <ArrowUp size={17} strokeWidth={1.6} />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}
